// Send for signature — the three steps that start a request.
//   1. What: Intake · Renewal · Transfer · LON · Other
//   2. Documents & signers: the kind's starting list (add / remove / upload),
//      supporting documents that ride along unsigned, language, the cycle the
//      request belongs to and therefore the Drive folder it files into, and
//      who signs (parent by email link or in person).
//   3. Review & send: what goes out, what comes back, then create — and email
//      the parent's link straight away if that's how they sign.
// Route: /packets/new?client=<uuid>[&kind=renewal][&cycle=<id>][&scope=doc]
//   kind   — skip the "What" step; from a submission step
//   cycle  — the submission this request belongs to (locked)
//   scope=doc — single documents only (LON, Other); from e-Signatures
// With no client at all (the global e-Signatures page): any document, to a
// recipient by email — an agency, a staff member, anyone. Files under
// 6 – Client Folder / _General / e-Signatures.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { CLIENT_DRIVE_ROOT, fileToBase64 } from '../lib/driveRoots';
import { listClientFolder, flattenBucket, fileKind } from '../lib/driveListing';
import { REQUEST_KINDS, GAPP_DOCS, UPLOAD_PARTIES, GENERAL_PARTIES, GENERAL_CLIENT_CODE, PARTY, createPacket, addUploadForm, addAttachments, prefillForms, missingRequired } from '../lib/documents/packets';
import { LANGS, langFromClient } from '../lib/documents/i18n';
import { listCycles, openCycle, createCycle, cycleLabel, cycleFolder, renewalDueFrom } from '../lib/paCycles';
import { sendSigningLink } from '../lib/documents/signingLinks';
import { fileName } from '../lib/documents/fileNames';
import FieldPlacer from '../components/signing/FieldPlacer';
import { placeLonFields } from '../lib/documents/lonLayout';
import ClientTrail, { tabCrumbs } from '../components/ClientTrail';

const KIND_ORDER = ['intake', 'renewal', 'transfer', 'lon', 'other'];
const ROLE_LABEL = { client: 'Parent', agency: 'Agency', rn: 'RN', witness: 'Witness', administrator: 'Admin', physician: 'Physician', external: 'Recipient' };

export default function SignatureRequestNew() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const clientId = sp.get('client');
  const lockedCycleId = sp.get('cycle') || null;
  const general = !clientId;                       // no client: recipient by email
  const docScope = sp.get('scope') === 'doc' || general;
  const [recipient, setRecipient] = useState({ name: '', email: '' });
  const [subject, setSubject] = useState('');

  const [client, setClient] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [authRows, setAuthRows] = useState([]);
  const [drive, setDrive] = useState(null);
  const [step, setStep] = useState(sp.get('kind') && REQUEST_KINDS[sp.get('kind')] ? 2 : 1);
  const [kind, setKind] = useState(REQUEST_KINDS[sp.get('kind')] ? sp.get('kind') : null);
  const [lang, setLang] = useState('en');
  const [cycleChoice, setCycleChoice] = useState('new');     // 'new' | <cycle id> | 'none'
  const [docs, setDocs] = useState([]);                       // [{ key, on }]
  const [uploads, setUploads] = useState([]);                 // [{ id, file, bytes, name, fields }]
  const [placing, setPlacing] = useState(null);               // upload id being placed
  const [attachments, setAttachments] = useState([]);         // [{ id, name, path, on }]
  const [picker, setPicker] = useState(false);
  const [parentMode, setParentMode] = useState('email');
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!clientId) { setClient({ ...{ id: null, client_id: GENERAL_CLIENT_CODE, first_name: '', last_name: '' } }); setStep(2); setKind('other'); return; }
    (async () => {
      const { data: c } = await supabase.from('clients').select('*').eq('id', clientId).single();
      if (!c) { setErr('Client not found.'); return; }
      setClient(c);
      setLang(langFromClient(c));
      setParentName([c.guardian_first_name, c.guardian_last_name].filter(Boolean).join(' ') || c.responsible_person_name || '');
      setParentEmail(c.guardian_email || c.emergency_email || '');
      const [cy, { data: ah }] = await Promise.all([
        listCycles(c.id),
        supabase.from('prior_auth_history').select('id, auth_type, auth_number, expiry_date, status').eq('client_id', c.id).eq('auth_type', 'PA').order('created_at', { ascending: false }),
      ]);
      setCycles(cy); setAuthRows(ah || []);
      if (lockedCycleId && cy.find((x) => x.id === lockedCycleId)) setCycleChoice(lockedCycleId);
      else if (openCycle(cy)) setCycleChoice(openCycle(cy).id);
      listClientFolder(c.client_id).then(setDrive).catch(() => setDrive({ folders: [], files: [] }));
    })();
  }, [clientId, lockedCycleId]);

  // Starting list for the chosen kind; suggested attachments from what's on Drive.
  useEffect(() => {
    if (!kind) return;
    const K = REQUEST_KINDS[kind];
    setDocs(K.forms.map((key) => ({ key, on: true })));
    setUploads([]);
    if (K.attachments && drive) {
      const all = driveFiles(drive);
      const picked = [];
      K.attachments.forEach((a) => {
        const hit = all.find((f) => a.match.test(f.name) && !picked.find((p) => p.id === f.id));
        if (hit) picked.push({ id: hit.id, name: hit.name, path: hit.bucket, on: true, suggested: a.label });
      });
      setAttachments(picked);
    } else setAttachments([]);
    // An LON or other document belongs to whichever cycle is current — open
    // if there is one, else the latest — never a new one.
    if (lockedCycleId) setCycleChoice(lockedCycleId);
    else if (!K.cycle_kind) setCycleChoice(openCycle(cycles)?.id || cycles.find((c) => c.status !== 'expired')?.id || 'none');
  }, [kind, drive, cycles, lockedCycleId]);
  const parties = general ? GENERAL_PARTIES : UPLOAD_PARTIES;
  const signerParty = general ? PARTY.EXTERNAL : PARTY.CLIENT;

  const K = kind ? REQUEST_KINDS[kind] : null;
  const chosenDocs = docs.filter((d) => d.on).map((d) => d.key);
  const onAttachments = attachments.filter((a) => a.on);
  const nextSeq = (cycles[0]?.sequence || 0) + 1;
  const newCycleKind = K?.cycle_kind || null;
  const newLabel = newCycleKind ? cycleLabel(newCycleKind, nextSeq) : null;
  const chosenCycle = cycleChoice === 'new' ? null : cycles.find((c) => c.id === cycleChoice) || null;
  const folderName = cycleChoice === 'none' ? 'Signing Packets' : cycleChoice === 'new' ? `PA Submissions/${newLabel}` : cycleFolder(chosenCycle);
  const currentPa = authRows.find((r) => r.status === 'active') || authRows[0];

  // signatures the request will carry
  const sigSummary = useMemo(() => {
    const by = {};
    chosenDocs.forEach((k) => (GAPP_DOCS[k]?.slots || []).forEach((s) => { by[s.party_role] = (by[s.party_role] || 0) + 1; }));
    uploads.forEach((u) => [...new Set(u.fields.map((f) => f.party))].forEach((p) => { by[p] = (by[p] || 0) + 1; }));
    return by;
  }, [chosenDocs, uploads]);
  const totalSigs = Object.values(sigSummary).reduce((a, b) => a + b, 0);
  const parentSigns = (sigSummary[signerParty] || 0) > 0;
  // What the coordinator still has to fill before the parent can be sent
  // anything — computed from what the record will pre-fill.
  const missing = useMemo(() => {
    if (!client || general) return [];
    const seed = prefillForms(client);
    return missingRequired({ client, forms: chosenDocs.map((k) => ({ form_key: k, field_data: seed[k] || {} })), gapp: true });
  }, [client, chosenDocs, general]);
  const sendNow = parentSigns && parentMode === 'email' && missing.length === 0;
  const signerEmail = general ? recipient.email : parentEmail;
  const signerName = general ? recipient.name : parentName;

  // ── uploads ──
  const pickFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!/pdf$/i.test(file.name) && file.type !== 'application/pdf') { setErr('Upload a PDF — scan or export the document to PDF first.'); return; }
    const bytes = new Uint8Array(await file.arrayBuffer());
    const id = `u${Date.now()}`;
    // An LON places its own fields from the words printed on it; anything
    // else, or a scan with no text, is placed by hand. Either way the placer
    // opens, so the sender sees where every box went.
    const fields = kind === 'lon' ? await placeLonFields(bytes).then((r) => r.fields).catch(() => []) : [];
    setUploads((u) => [...u, { id, file, bytes, name: file.name, fields }]);
    setPlacing(id);
  };
  const setFields = (id, fields) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, fields } : x)));

  // ── create ──
  const create = async () => {
    setBusy('create'); setErr('');
    try {
      let cycle = chosenCycle;
      if (cycleChoice === 'new') {
        cycle = await createCycle({ client, kind: newCycleKind, dueDate: newCycleKind === 'renewal' ? renewalDueFrom(currentPa?.expiry_date) : null, profile, existing: cycles });
      }
      const packet = await createPacket({
        clientId: client.id, packetType: K.packet_type, client: general ? null : client, language: lang,
        formKeys: chosenDocs, kind, cycleId: cycle?.id || null, title: K.label,
        recipient: general ? { name: recipient.name.trim(), email: recipient.email.trim().toLowerCase() } : null,
        subject: general ? subject.trim() : null,
      });
      // Documents with placed fields: a file picked from Drive is referenced
      // where it is; one from the computer is uploaded to the request folder.
      for (const u of uploads) {
        let sourceFileId = u.driveFileId;
        if (!sourceFileId) {
          const { data, error } = await supabase.functions.invoke('upload-to-drive', {
            body: { fileBase64: await fileToBase64(u.file), filename: u.name, clientId: client.client_id, rootFolderId: CLIENT_DRIVE_ROOT, subfolder: cycle ? cycleFolder(cycle) : general ? 'e-Signatures' : 'Signing Packets', contentType: 'application/pdf' },
          });
          if (error || !data?.success) throw new Error(error?.message || data?.error || `Could not upload ${u.name}`);
          sourceFileId = data.fileId;
        }
        await addUploadForm(packet.id, { sourceFileId, name: u.name, fields: u.fields });
      }
      if (onAttachments.length) await addAttachments(packet.id, onAttachments.map((a) => ({ id: a.id, name: a.name, path: a.path })));
      if (sendNow) {
        try { await sendSigningLink({ packet, client, role: signerParty, email: signerEmail, lang, message, recipientName: signerName }); }
        catch (e) { setErr(`Created, but the email did not go: ${e.message}. Resend it from the request.`); }
      }
      navigate(`/packets/${packet.id}`, { replace: true });
    } catch (e) { setErr(e.message || String(e)); }
    setBusy('');
  };

  if (err && !client) return <div style={{ padding: 24, color: 'var(--danger)' }}>{err}</div>;
  if (!client) return <div style={{ padding: 24 }}>Loading…</div>;

  return (
    <div style={{ padding: '0 16px 40px', maxWidth: 1040, margin: '0 auto' }}>
      {general
        ? <ClientTrail root={[{ label: 'e-Signatures', to: '/esign' }]} crumbs={[{ label: 'Send for e-signature' }]} />
        : <ClientTrail client={client} crumbs={[...tabCrumbs(client.id, 'documents', lockedCycleId ? 'submissions' : 'esign'), { label: 'Send for e-signature' }]} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Send for e-signature</div>
          <div style={{ fontSize: 12.5, color: 'var(--text3)' }}>{general ? 'Not tied to a client' : `${client.last_name}, ${client.first_name} · ${client.client_id}`}{K ? ` · ${K.label}` : ''}</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(general ? '/esign' : `/clients/${client.id}?tab=documents${lockedCycleId ? '' : '&sub=esign'}`)}>← Back</button>
      </div>
      <Steps step={step} />
      {err && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger)', padding: '8px 12px', borderRadius: 8, fontSize: 13, margin: '10px 0' }}>{err}</div>}

      {/* ── 1 ── */}
      {step === 1 && docScope && <p className="hint" style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text3)' }}>A single document for someone to sign. The family forms for a submission are sent from <b>GAPP Submissions</b>, on the step that needs them.</p>}
      {step === 1 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10, marginTop: 14 }}>
          {(docScope ? ['lon', 'other'] : KIND_ORDER).map((k) => {
            const d = REQUEST_KINDS[k];
            const n = d.forms.length;
            const sigs = d.forms.reduce((a, key) => a + (GAPP_DOCS[key]?.slots.length || 0), 0);
            return (
              <button key={k} type="button" onClick={() => { setKind(k); setStep(2); }}
                style={{ textAlign: 'left', font: 'inherit', cursor: 'pointer', border: '1px solid var(--border2)', borderRadius: 10, padding: 14, background: 'var(--card-bg)', color: 'var(--text)', display: 'grid', gap: 4, alignContent: 'start' }}>
                <b style={{ fontSize: 14 }}>{d.label}</b>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{d.blurb}</span>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{d.upload ? 'you place the fields' : `${n} documents · ${sigs} signatures${d.attachments ? ` + ${d.attachments.length} attached` : ''}`}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── 2 ── */}
      {step === 2 && K && (
        <div style={{ display: 'grid', gap: 14, marginTop: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Language" hint="Applies to our own documents, the signing page and the email; state forms use the official Spanish where DCH publishes one.">
              <Seg value={lang} onChange={setLang} options={LANGS.map((l) => [l.code, l.label])} />
            </Field>
            {general ? (
              <Field label="Subject" hint="Names the request and the files. Files to _General / e-Signatures.">
                <input className="form-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Staffing agreement — Extended Living Healthcare" />
              </Field>
            ) : (
            <Field label="Belongs to" hint={`Files to ${folderName}`}>
              {lockedCycleId && chosenCycle ? <div className="form-input" style={{ background: 'var(--bg2)' }}>{chosenCycle.label}</div> :
              <select className="form-select" value={cycleChoice} onChange={(e) => setCycleChoice(e.target.value)}>
                {newCycleKind && <option value="new">New submission — {newLabel}</option>}
                {cycles.filter((c) => c.status !== 'expired').map((c) => <option key={c.id} value={c.id}>{c.label} · {c.status === 'preparing' ? 'in preparation' : c.status}</option>)}
                <option value="none">No submission — Signing Packets folder</option>
              </select>}
            </Field>
            )}
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '9px 14px', background: 'var(--bg2)', fontSize: 12.5, fontWeight: 600, color: 'var(--text2)', flexWrap: 'wrap' }}>
              <span>{chosenDocs.length + uploads.length} document{chosenDocs.length + uploads.length === 1 ? '' : 's'} to sign · {totalSigs} signature{totalSigs === 1 ? '' : 's'}{onAttachments.length ? ` · ${onAttachments.length} attached` : ''}</span>
              <span style={{ display: 'flex', gap: 6 }}>
                {!general && <button className="btn btn-ghost btn-sm" onClick={() => setPicker(true)}>+ Add from Drive</button>}
                <label className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>+ Upload PDF<input type="file" accept="application/pdf,.pdf" onChange={pickFile} style={{ display: 'none' }} /></label>
              </span>
            </div>
            {docs.map((d) => {
              const g = GAPP_DOCS[d.key];
              return (
                <label key={d.key} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: 10, alignItems: 'center', padding: '8px 14px', borderTop: '1px solid var(--border)', opacity: d.on ? 1 : 0.5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={d.on} onChange={(e) => setDocs((arr) => arr.map((x) => (x.key === d.key ? { ...x, on: e.target.checked } : x)))} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>{g?.form_title || d.key}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--accent)', fontWeight: 600 }}>{g?.slots.length ? g.slots.map((s) => s.signer_label).join(' · ') : 'no signature — filled from the record'}</div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{['phi_release', 'nacp_signature'].includes(d.key) ? 'AHFC document' : 'DCH original'}</span>
                </label>
              );
            })}
            {uploads.map((u) => (
              <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: 10, alignItems: 'center', padding: '8px 14px', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)' }}>PDF</span>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>{u.name}</div>
                  <div style={{ fontSize: 11.5, color: u.fields.length ? 'var(--accent)' : 'var(--warn)', fontWeight: 600 }}>
                    {u.fields.length ? `${u.fields.length} field${u.fields.length === 1 ? '' : 's'} · ${[...new Set(u.fields.map((f) => f.party))].map((p) => ROLE_LABEL[p] || p).join(', ')}` : 'no fields placed yet'}
                  </div>
                </div>
                <span style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setPlacing(u.id)}>Place fields</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setUploads((arr) => arr.filter((x) => x.id !== u.id))}>Remove</button>
                </span>
              </div>
            ))}
            {attachments.map((a) => (
              <label key={a.id} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: 10, alignItems: 'center', padding: '8px 14px', borderTop: '1px solid var(--border)', background: 'var(--bg2)', opacity: a.on ? 1 : 0.5, cursor: 'pointer' }}>
                <input type="checkbox" checked={a.on} onChange={(e) => setAttachments((arr) => arr.map((x) => (x.id === a.id ? { ...x, on: e.target.checked } : x)))} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>{a.name} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text3)' }}>· not signed, attached</span></div>
                  <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>{a.suggested ? `${a.suggested} · ` : ''}from {a.path || 'Drive'}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>from Drive</span>
              </label>
            ))}
            {K.attachments && K.attachments.filter((x) => !attachments.find((a) => a.suggested === x.label)).map((x) => (
              <div key={x.key} style={{ padding: '8px 14px 8px 46px', borderTop: '1px solid var(--border)', background: 'var(--bg2)', fontSize: 12.5, color: 'var(--warn)' }}>
                ○ {x.label} — nothing matching on Drive. <button type="button" onClick={() => setPicker(true)} style={{ background: 'none', border: 'none', color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', padding: 0 }}>Pick a file</button>
              </div>
            ))}
            {chosenDocs.length + uploads.length === 0 && (
              <div style={{ padding: '12px 14px', borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text3)' }}>Nothing to sign yet — upload a PDF and place the fields, or add a file from Drive.</div>
            )}
          </div>

          {(parentSigns || general) && (
            <Field label={general ? 'Recipient' : 'Parent / caregiver'}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                <input className="form-input" style={{ flex: '1 1 160px' }} value={general ? recipient.name : parentName} onChange={(e) => (general ? setRecipient((r) => ({ ...r, name: e.target.value })) : setParentName(e.target.value))} placeholder="Name" />
                <Seg value={parentMode} onChange={setParentMode} options={[['email', 'Email a link'], ['person', 'Sign in person']]} />
                {parentMode === 'email'
                  ? <input className="form-input" style={{ flex: '1 1 200px' }} type="email" value={general ? recipient.email : parentEmail} onChange={(e) => (general ? setRecipient((r) => ({ ...r, email: e.target.value })) : setParentEmail(e.target.value))} placeholder="Email" />
                  : <span style={{ fontSize: 12, color: 'var(--text3)' }}>Hand them the device on the request page.</span>}
              </div>
            </Field>
          )}
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            Staff signatures — {Object.entries(sigSummary).filter(([r]) => r !== PARTY.CLIENT && r !== PARTY.EXTERNAL).map(([r, n]) => `${ROLE_LABEL[r] || r} ${n}`).join(' · ') || 'none'} — are signed in CAERA on the request page, before or after the {general ? 'recipient' : 'parent'}.
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <button className="btn btn-outline" onClick={() => setStep(1)}>Back</button>
            <button className="btn btn-primary" disabled={chosenDocs.length + uploads.length === 0 || uploads.some((u) => !u.fields.length) || (general && (!subject.trim() || !recipient.name.trim()))} onClick={() => setStep(3)}>Review →</button>
          </div>
        </div>
      )}

      {/* ── 3 ── */}
      {step === 3 && K && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 14 }}>
          <div>
            <dl style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px', fontSize: 13, margin: 0 }}>
              <dt style={{ color: 'var(--text3)' }}>Request</dt><dd style={{ margin: 0 }}><b>{K.label}</b> · {LANGS.find((l) => l.code === lang)?.label}</dd>
              <dt style={{ color: 'var(--text3)' }}>To sign</dt><dd style={{ margin: 0 }}>{chosenDocs.length + uploads.length} documents · {totalSigs} signatures · {Object.entries(sigSummary).map(([r, n]) => `${(ROLE_LABEL[r] || r).toLowerCase()} ${n}`).join(', ')}</dd>
              {onAttachments.length > 0 && <><dt style={{ color: 'var(--text3)' }}>Attached</dt><dd style={{ margin: 0 }}>{onAttachments.length} supporting document{onAttachments.length === 1 ? '' : 's'}, not signed</dd></>}
              {parentSigns && <><dt style={{ color: 'var(--text3)' }}>{general ? 'Recipient' : 'Parent'}</dt><dd style={{ margin: 0 }}>{signerName || '—'} · {parentMode === 'email' ? `link to ${signerEmail || '(no email)'}` : 'signs in person'}</dd></>}
              <dt style={{ color: 'var(--text3)' }}>Files to</dt><dd style={{ margin: 0 }}>{folderName}</dd>
              {!general && <><dt style={{ color: 'var(--text3)' }}>Prefilled from</dt><dd style={{ margin: 0 }}>client record{currentPa?.auth_number ? `, PA ${currentPa.auth_number}` : ''}</dd></>}
            </dl>
            {missing.length > 0 && (
              <div style={{ marginTop: 12, border: '1px solid var(--warn-border)', background: 'var(--warn-bg)', borderRadius: 10, padding: '10px 14px', fontSize: 12.5 }}>
                <b style={{ color: 'var(--warn)' }}>{missing.length} field{missing.length === 1 ? '' : 's'} to fill before this can go to the parent</b>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: 'var(--text2)' }}>
                  {missing.slice(0, 8).map((m) => <li key={`${m.form_key}:${m.key}`}>{m.form_key === 'client' ? 'Client record' : GAPP_DOCS[m.form_key]?.form_title?.split(' — ')[0] || m.form_key} — {m.label}</li>)}
                  {missing.length > 8 && <li>… and {missing.length - 8} more</li>}
                </ul>
                <div style={{ marginTop: 6, color: 'var(--text3)' }}>Create the request, fill them on the request page, and send the parent's link from there.</div>
              </div>
            )}
            {sendNow && (
              <Field label={`Message to parent (${lang === 'es' ? 'Spanish' : 'English'})`} style={{ marginTop: 12 }}>
                <textarea className="form-textarea" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional — added to the email above the button." />
              </Field>
            )}
          </div>
          <div>
            <div style={{ border: '1px dashed var(--border2)', borderRadius: 10, padding: '12px 14px', fontSize: 12.5 }}>
              <b>What comes back when everyone has signed</b>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {chosenDocs.map((k) => <li key={k}>{fileName([client.client_id, new Date().toISOString().slice(0, 7), shortName(k), GAPP_DOCS[k]?.slots.length ? 'signed' : ''])}</li>)}
                {uploads.map((u) => <li key={u.id}>{fileName([general ? subject.trim() : client.client_id, new Date().toISOString().slice(0, 7), u.name.replace(/\.pdf$/i, ''), 'signed'])}</li>)}
                <li style={{ color: 'var(--text3)' }}>{fileName([general ? subject.trim() : client.client_id, new Date().toISOString().slice(0, 7), 'Signature certificate', K?.label || 'Any document'])}</li>
              </ul>
              <p style={{ fontSize: 12, color: 'var(--text3)', margin: '8px 0 0' }}>One file per document, each the untouched original with the fields filled and the signatures drawn. Never merged.</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 14 }}>
              <button className="btn btn-outline" onClick={() => setStep(2)}>Back</button>
              <button className="btn btn-primary" disabled={busy === 'create' || (sendNow && !/@/.test(signerEmail))} onClick={create}>
                {busy === 'create' ? 'Creating…' : sendNow ? `Create & send to ${signerName || 'recipient'}` : missing.length ? 'Create — fill fields before sending' : 'Create request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* field placer */}
      {placing && (() => {
        const u = uploads.find((x) => x.id === placing);
        if (!u) return null;
        return (
          <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && setPlacing(null)}>
            <div className="modal" style={{ maxWidth: 1000, width: '96vw' }} onMouseDown={(e) => e.stopPropagation()}>
              <div className="modal-header"><div className="modal-title">Place fields — {u.name}</div><button className="btn btn-ghost btn-sm" onClick={() => setPlacing(null)}>✕</button></div>
              <div className="modal-body" style={{ maxHeight: '75vh', overflow: 'auto' }}>
                <FieldPlacer bytes={u.bytes} fields={u.fields} onChange={(f) => setFields(u.id, f)} parties={parties} defaultParty={signerParty} />
              </div>
              <div className="modal-footer"><button className="btn btn-primary" onClick={() => setPlacing(null)}>Done</button></div>
            </div>
          </div>
        );
      })()}

      {/* drive picker */}
      {picker && (
        <DrivePicker drive={drive} onClose={() => setPicker(false)} onPick={(f, how) => {
          if (how === 'attach') setAttachments((arr) => (arr.find((a) => a.id === f.id) ? arr : [...arr, { id: f.id, name: f.name, path: f.bucket, on: true }]));
          else {
            // sign it: fetch bytes, then place fields
            setBusy('fetch');
            supabase.functions.invoke('drive-file', { body: { action: 'download', fileId: f.id } }).then(({ data, error }) => {
              if (error || !data?.success) throw new Error(error?.message || data?.error || 'Could not fetch the file');
              const bytes = Uint8Array.from(atob(data.base64), (c) => c.charCodeAt(0));
              const id = `u${Date.now()}`;
              setUploads((u) => [...u, { id, driveFileId: f.id, bytes, name: f.name, fields: [] }]);
              setPlacing(id);
            }).catch((e) => setErr(e.message)).finally(() => setBusy(''));
          }
          setPicker(false);
        }} />
      )}
    </div>
  );
}

const shortName = (k) => ({ phi_release: 'PHI Release', nacp_signature: 'Assessment sign-off' }[k] || (GAPP_DOCS[k]?.form_title || k).split(' — ')[0]);
const driveFiles = (drive) => [...(drive?.folders || []).flatMap((b) => flattenBucket(b).filter((f) => !f.mimeType?.includes('folder')).map((f) => ({ ...f, bucket: b.name }))), ...(drive?.files || []).map((f) => ({ ...f, bucket: '' }))];

function Steps({ step }) {
  const S = ['What', 'Documents & signers', 'Review & send'];
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12.5, color: 'var(--text3)', flexWrap: 'wrap' }}>
      {S.map((s, i) => (
        <React.Fragment key={s}>
          <span style={{ padding: '4px 10px', borderRadius: 20, background: step === i + 1 ? 'var(--accent)' : 'transparent', color: step === i + 1 ? '#fff' : step > i + 1 ? 'var(--success)' : 'var(--text3)', fontWeight: step >= i + 1 ? 600 : 400 }}>
            {step > i + 1 ? '✓ ' : `${i + 1} `}{s}
          </span>
          {i < S.length - 1 && <span style={{ color: 'var(--border2)' }}>›</span>}
        </React.Fragment>
      ))}
    </div>
  );
}
function Field({ label, hint, children, style }) {
  return <div style={{ display: 'grid', gap: 4, ...style }}><span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600 }}>{label}</span>{children}{hint && <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{hint}</span>}</div>;
}
function Seg({ value, onChange, options }) {
  return (
    <div style={{ display: 'inline-grid', gridAutoFlow: 'column', gridAutoColumns: '1fr', width: 'max-content', border: '1px solid var(--border2)', borderRadius: 8, overflow: 'hidden' }}>
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} style={{ font: 'inherit', fontSize: 12.5, padding: '6px 16px', minWidth: 96, border: 'none', cursor: 'pointer', background: value === v ? 'var(--accent)' : 'transparent', color: value === v ? '#fff' : 'var(--text2)', fontWeight: value === v ? 600 : 400 }}>{l}</button>
      ))}
    </div>
  );
}
function DrivePicker({ drive, onClose, onPick }) {
  const [q, setQ] = useState('');
  const files = driveFiles(drive).filter((f) => !q || f.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header"><div className="modal-title">Add from the client's Drive folder</div><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <input className="form-input" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 10 }} />
          {!drive && <div style={{ fontSize: 13, color: 'var(--text3)' }}>Reading the folder…</div>}
          {files.map((f) => (
            <div key={f.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, width: 40, textAlign: 'center', border: '1px solid var(--border)', borderRadius: 4, flexShrink: 0 }}>{fileKind(f.mimeType, f.name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{f.bucket}{f.path ? ` / ${f.path}` : ''}</div>
              </div>
              <button className="btn btn-outline btn-sm" onClick={() => onPick(f, 'attach')}>Attach</button>
              {/pdf/i.test(f.mimeType || f.name) && <button className="btn btn-primary btn-sm" onClick={() => onPick(f, 'sign')}>Sign it</button>}
            </div>
          ))}
          {drive && files.length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)' }}>Nothing matches.</div>}
        </div>
      </div>
    </div>
  );
}
